/**
 * The right panel. INBOX scope item 11, the rendering half.
 *
 * ⛔ **IDENTITY, NOTES AND ACTIVITY. NOTHING ELSE, AND NOTHING EMPTY.** Booking
 * blocks, tags, pipeline stage, field groups and running flows are a NAMED GAP
 * pointing at CRM-BUILD and FLOW-BUILDER - and the gap is named once, at the
 * bottom of the panel, rather than as five cards saying "none".
 *
 * ⚠️ THE DIFFERENCE IS NOT COSMETIC. An empty Bookings card says *this customer
 * has no bookings*. The named gap says *this product does not track bookings
 * yet*. One of those is a claim about the customer and it is false.
 */
import { activityRender, assigneeRender, personName, snoozeRender } from './panel-view.js'
import type { ConversationPanel } from '../../../src/services/conversation-panel.js'

export type RightPanelProps = {
  /** `null` means not loaded yet, which is NOT the same as an absent panel. */
  panel: ConversationPanel | null | undefined
  error?: string | null
}

/** The verbs, in the component, so the pure module never holds copy. */
function activityLine(event: ConversationPanel['activity'][number]): string {
  const r = activityRender(event)
  const who = r.actor ?? 'Someone'
  switch (r.kind) {
    case 'statusChanged':
      return `${who} marked this ${r.to}`
    case 'assigned':
      // Two sentences from one event type, decided by `from`. The first
      // assignment and a handover are different facts.
      return r.from === null
        ? `${who} assigned this to ${r.to ?? 'someone'}`
        : `${who} moved this from ${r.from} to ${r.to ?? 'someone'}`
    case 'unassigned':
      return r.from === null ? `${who} unassigned this` : `${who} unassigned ${r.from}`
    case 'unknown':
      /**
       * ⛔ NAMED, NOT DROPPED. A later phase's event type reaches this branch,
       * and a feed that silently omits rows it does not recognise is the
       * missed-event failure with a tidy UI. The raw verb is ugly and readable,
       * which is the right trade for a line nobody has written copy for yet.
       */
      return `${who}: ${r.eventType}`
  }
}

export function RightPanel({ panel, error = null }: RightPanelProps): React.JSX.Element {
  if (error !== null) {
    return (
      <div className="panel-state" role="alert" data-testid="panel-error">
        Could not load this customer.
      </div>
    )
  }

  // ⛔ FOUR STATES, AND THE FIRST TWO ARE NOT THE SAME. `undefined` is "no
  // conversation is open", `null` is "asked and nothing came back". Collapsing
  // them makes an unopened panel look like a broken one.
  if (panel === undefined) {
    return <div className="panel-state" data-testid="panel-none" />
  }

  if (panel === null) {
    return (
      <div className="panel-state" data-testid="panel-empty">
        No customer to show
      </div>
    )
  }

  const { identity } = panel
  const assignee = assigneeRender(identity)
  const snooze = snoozeRender(identity)
  const name = personName(identity.displayName, null) ?? identity.phoneNumber ?? 'Guest'

  return (
    <aside className="panel" data-testid="panel">
      <section className="panel__card" data-testid="panel-identity">
        <h2 className="panel__name">{name}</h2>

        {/* ⛔ OMITTED when absent, never rendered as a dash. A placeholder sits
            where a real number sits. Same rule as the list's timestamp. */}
        {identity.phoneNumber !== null && identity.phoneNumber !== identity.displayName && (
          <p className="panel__line">{identity.phoneNumber}</p>
        )}

        <dl className="panel__facts">
          <dt className="panel__key">Channel</dt>
          <dd className="panel__value">{identity.channel}</dd>

          <dt className="panel__key">Status</dt>
          <dd className="panel__value">{identity.status}</dd>

          <dt className="panel__key">Assigned</dt>
          {/* ⚠️ Unassigned gets a WORD, not an omission: it is a real state
              rather than a gap in what we know. See `assigneeRender`. */}
          <dd className="panel__value" data-testid="panel-assignee">
            {assignee.kind === 'assigned' ? assignee.name : 'Nobody'}
          </dd>
        </dl>

        {snooze.kind === 'snoozed' && (
          <p className="panel__snooze" data-testid="panel-snooze">
            Snoozed{snooze.note !== null ? `: ${snooze.note}` : ''}
          </p>
        )}
      </section>

      {/*
        ┌─ ⛔ THE NOTES CARD IS DELIBERATELY ABSENT ─────────────────────────────┐
        │ It rendered until 2026-08-11, reading the SAME conversation-scoped     │
        │ rows that item 9 now renders inline in the thread. The identical notes │
        │ appeared twice on one screen, and the plan's thread line rejects that  │
        │ by name: notes go inline *"not in a side log - they are part of the    │
        │ conversation's narrative, and moving them elsewhere means reading two  │
        │ places to follow one story."*                                          │
        │                                                                       │
        │ ⚠️ THE PANEL'S "5. Notes" IS CONTACT-LEVEL NOTES, WHICH ARE A          │
        │ DIFFERENT THING. The plan defines them separately: a contact note      │
        │ ("always books with her partner, prefers morning dives") *"persists    │
        │ and shows on EVERY conversation with that contact"*, while a           │
        │ conversation note belongs to one thread. Both readings were open until │
        │ Amir ruled; this is the ruling.                                        │
        │                                                                       │
        │ ⛔ AND IT IS NOT REPOINTED AT CONTACT NOTES, BECAUSE THERE ARE NONE.   │
        │ No table, no column, no producer, and `messages.conversation_id` is    │
        │ NOT NULL so they cannot live where conversation notes live. Repointing │
        │ the query would leave a card whose only demonstrated state is empty -  │
        │ **a contact with no notes and a feature that does not exist look the   │
        │ same**, and this phase has applied that rule four times already.       │
        │                                                                       │
        │ Owner is COLLABORATION (plan line 692, Phase 3). Recorded as           │
        │ `contact-level-notes-have-no-storage` in `ops/deferrals.txt`.          │
        │                                                                       │
        │ ✅ SPENT 2026-09-12, AND KEPT AS HISTORY RATHER THAN DELETED. Migration │
        │ `0030` gave contact notes a table, so "there are none" stopped being   │
        │ true and the card is built below. **A premise DISCHARGED BY LATER WORK │
        │ is a different thing from one that was WRONG**, and this phase has     │
        │ recorded both - the URL scheme's premise was never true and this one   │
        │ was true when written.                                                │
        │                                                                       │
        │ ⚠️ THE FIRST HALF IS STILL LOAD-BEARING: the card shows CONTACT notes,  │
        │ never the conversation's own. Those render inline in the thread, and   │
        │ showing them here too is the duplication this comment opens by naming. │
        └───────────────────────────────────────────────────────────────────────┘
      */}

      {/**
       * ⛔ THE NOTES CARD, BUILT. Contact notes follow the person into every
       * thread, which is exactly what the ruling above says the panel's
       * "5. Notes" means.
       *
       * ⚠️ THE EMPTY STATE SAYS WHICH KIND OF NOTE IS MISSING, because the
       * thread beside it may be full of conversation notes - "No notes" over a
       * screen of visible notes reads as a defect rather than as a fact about
       * the person.
       */}
      <section className="panel__card" data-testid="panel-notes">
        <h3 className="panel__heading">Notes</h3>
        {panel.notes.length === 0 ? (
          <p className="panel__none">No notes about this contact</p>
        ) : (
          <ul className="panel__list">
            {panel.notes.map((note) => (
              <li
                key={note.id}
                className={note.pinnedAt === null ? 'panel__note' : 'panel__note panel__note--pinned'}
                data-testid="panel-note"
              >
                <p className="panel__note-body">{note.body}</p>
                <p className="panel__note-by">
                  {/* ⛔ An automation is named as one. `actor_type` exists so a
                      machine's work is not attributed to a person. */}
                  {note.actorType === 'agent'
                    ? [note.authorFirstName, note.authorLastName].filter(Boolean).join(' ') ||
                      'an agent'
                    : 'Automated'}
                  {' · '}
                  {note.createdAtDisplay}
                  {note.editedAt !== null && ' · edited'}
                  {note.pinnedAt !== null && ' · pinned'}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel__card" data-testid="panel-activity">
        <h3 className="panel__heading">Activity</h3>
        {panel.activity.length === 0 ? (
          <p className="panel__none">No activity yet</p>
        ) : (
          <ul className="panel__list">
            {panel.activity.map((event) => (
              <li key={event.id} className="panel__event">
                {activityLine(event)}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/*
        ⛔ THE NAMED GAP, ONCE, RATHER THAN FIVE EMPTY CARDS.

        Item 11: *"Booking blocks, tags, pipeline stage, field groups and running
        flows are a named gap pointing at CRM-BUILD and FLOW-BUILDER. No empty
        cards."* An empty Bookings card is a claim about the CUSTOMER; this is a
        statement about the PRODUCT, and only one of them is true.
      */}
      <p className="panel__gap" data-testid="panel-gap">
        Bookings, tags, pipeline stage and flows are not built yet.
      </p>
    </aside>
  )
}
